/**
 * A REGRA de apresentação do quadro da Correção CFC (000384): o que cada célula mostra. Puro, sem
 * import, com prova própria (`tests/quadro-cfc.mjs`) — a página pública e o arquivo PNG que ela baixa
 * desenham pela mesma função, e duas cópias divergiriam.
 *
 * A alternativa vem do banco já normalizada (A, B, C, D, A/R a D/R, ANULADO ou RECURSO), e só quando a
 * equipe a liberou: o banco não manda ao navegador a alternativa escondida.
 *
 * O quadro de lá mostrava a PRIMEIRA LETRA de tudo que não fosse "x/R", então ANULADO aparecia como
 * "A" — a mesma letra da alternativa A, só que em vermelho. Aqui ANULADO é "X", e a legenda diz.
 */

export type Celula = {
  /** O que vai escrito na célula; vazio quando a questão ainda não foi liberada. */
  texto: string;
  /** Recurso ou anulação: a célula vai em vermelho, como no quadro de lá. */
  alerta: boolean;
};

const COM_RECURSO = new Set(["A/R", "B/R", "C/R", "D/R"]);
const LETRAS = new Set(["A", "B", "C", "D"]);

export function celulaDoQuadro(alternativa: string | null | undefined): Celula {
  const a = (alternativa ?? "").trim().toUpperCase();
  if (a === "") return { texto: "", alerta: false };
  if (COM_RECURSO.has(a)) return { texto: a, alerta: true };
  if (a === "RECURSO") return { texto: "R", alerta: true };
  if (a === "ANULADO") return { texto: "X", alerta: true };
  if (LETRAS.has(a)) return { texto: a, alerta: false };
  // Valor que o banco não deveria mandar (a constraint só aceita os dez acima). Mostrar cru é melhor
  // que esconder: a célula vazia diria "ainda não liberada", que seria falso.
  return { texto: a, alerta: false };
}

/** O número da questão como o quadro de lá escreve: 01 a 09 com zero, e o resto como vem. */
export function numeroNoQuadro(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * As cores fixas de cada caderno, as do quadro de lá (o cinza, o azul, o dourado e o verde), com a
 * borda das células um tom abaixo. Cor FIXA, e não token: o quadro é PAPEL — ele vai para a
 * transmissão e para o PNG, e não troca com o tema de quem o abre.
 */
export const CORES_DO_CADERNO: { fundo: string; borda: string }[] = [
  { fundo: "#787878", borda: "#5f5f5f" },
  { fundo: "#336699", borda: "#2a4968" },
  { fundo: "#cc9933", borda: "#a37b2a" },
  { fundo: "#669966", borda: "#5b7a5b" },
];

export function corDoCaderno(ordem: number) {
  return CORES_DO_CADERNO[(Math.max(1, ordem) - 1) % CORES_DO_CADERNO.length];
}

/** As cores da célula: branca com letra preta; em alerta, rosa com letra vermelha (as de lá). */
export const COR_DA_CELULA = { fundo: "#ffffff", tinta: "#000000", fundoAlerta: "#ffc7ce", tintaAlerta: "#9e060e" };

/** Quantas linhas por coluna: o quadro de lá enche colunas de 10, de cima para baixo. */
export const LINHAS_POR_COLUNA = 10;
